import React, { useEffect, useRef, useState } from 'react';

// Desktop-only animated cursor: glowing dot + lagging halo ring.
// - Activates ONLY when JS runs on a fine-pointer, motion-OK, >=768px viewport;
//   otherwise the native cursor is untouched (html.custom-cursor-active gates cursor:none).
// - Position updates are transform/translate3d driven by rAF (no layout thrash).
// - pointer-events: none on every cursor element — clicks are never blocked.
const LERP = 0.15;

const INTERACTIVE_SELECTOR = [
  'button', 'a', 'select', 'label', 'summary',
  '[role="button"]', '[role="tab"]', '[role="menuitem"]', '[role="option"]',
  '[role="switch"]', '[role="checkbox"]', '[role="combobox"]', '[role="slider"]',
  '[draggable="true"]', '.cursor-pointer', '.cursor-grab',
].join(', ');

const IBEAM_SELECTOR = [
  'input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]):not([type="button"]):not([type="submit"])',
  'textarea', '[contenteditable="true"]',
].join(', ');

const cursorSupported = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(pointer: fine)').matches &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
  window.matchMedia('(min-width: 768px)').matches;

export const CustomCursor = () => {
  const [enabled, setEnabled] = useState(cursorSupported);
  const dotRef = useRef(null);
  const haloRef = useRef(null);

  // Re-evaluate when pointer type, motion preference, or viewport changes
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mqs = [
      window.matchMedia('(pointer: fine)'),
      window.matchMedia('(prefers-reduced-motion: reduce)'),
      window.matchMedia('(min-width: 768px)'),
    ];
    const update = () => setEnabled(cursorSupported());
    mqs.forEach((mq) => mq.addEventListener('change', update));
    return () => mqs.forEach((mq) => mq.removeEventListener('change', update));
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const dot = dotRef.current;
    const halo = haloRef.current;
    if (!dot || !halo) return undefined;

    document.documentElement.classList.add('custom-cursor-active');

    const pos = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const haloPos = { x: pos.x, y: pos.y };
    let visible = false;
    let raf = 0;

    const show = () => {
      if (visible) return;
      visible = true;
      dot.style.opacity = '1';
      halo.style.opacity = '1';
    };
    const hide = () => {
      visible = false;
      dot.style.opacity = '0';
      halo.style.opacity = '0';
    };
    hide();

    const track = (e) => {
      pos.x = e.clientX;
      pos.y = e.clientY;
      dot.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0)`;
      show();
    };

    // Hover intent: delegated, cheap closest() checks only when the target changes
    const onOver = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest(IBEAM_SELECTOR)) {
        halo.classList.add('is-ibeam');
        halo.classList.remove('is-hover');
      } else if (t.closest(INTERACTIVE_SELECTOR)) {
        halo.classList.add('is-hover');
        halo.classList.remove('is-ibeam');
      } else {
        halo.classList.remove('is-hover', 'is-ibeam');
      }
    };

    const onDown = (e) => {
      const ripple = document.createElement('div');
      ripple.className = 'cursor-ripple';
      ripple.style.left = `${e.clientX}px`;
      ripple.style.top = `${e.clientY}px`;
      document.body.appendChild(ripple);
      setTimeout(() => ripple.remove(), 500);
    };

    const onLeave = () => hide();
    const onEnter = () => show();

    const loop = () => {
      haloPos.x += (pos.x - haloPos.x) * LERP;
      haloPos.y += (pos.y - haloPos.y) * LERP;
      halo.style.transform = `translate3d(${haloPos.x}px, ${haloPos.y}px, 0)`;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    document.addEventListener('mousemove', track, { passive: true });
    document.addEventListener('dragover', track, { passive: true }); // keep tracking during kanban drags
    document.addEventListener('mouseover', onOver, { passive: true });
    document.addEventListener('mousedown', onDown, { passive: true });
    document.documentElement.addEventListener('mouseleave', onLeave);
    document.documentElement.addEventListener('mouseenter', onEnter);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousemove', track);
      document.removeEventListener('dragover', track);
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mousedown', onDown);
      document.documentElement.removeEventListener('mouseleave', onLeave);
      document.documentElement.removeEventListener('mouseenter', onEnter);
      document.documentElement.classList.remove('custom-cursor-active');
      document.querySelectorAll('.cursor-ripple').forEach((n) => n.remove());
    };
  }, [enabled]);

  if (!enabled) return null;
  return (
    <>
      <div ref={dotRef} data-testid="custom-cursor-dot" aria-hidden="true" className="cursor-dot" style={{ opacity: 0 }} />
      <div ref={haloRef} data-testid="custom-cursor-halo" aria-hidden="true" className="cursor-halo-wrap" style={{ opacity: 0 }}>
        <div className="cursor-halo-ring" />
      </div>
    </>
  );
};
